'use client';

// Shared clock-tick hook — drives a periodic re-render in every subscriber so
// time-dependent derivations over realtime data re-run as the clock advances,
// WITHOUT each consuming view wiring its own `setInterval`.
//
// Why this exists (BUG-887). A lot's lifecycle phase is derived on the client
// via `deriveLotPhase` (ADR 023) — a pure function whose result depends on the
// current time (a lot is `pre-bidding`/`open-for-bidding` only *before* its
// close, `awaiting-resolution` *after*). Snapshot subscriptions (`useSnapshot`)
// re-render a consumer when a NEW broadcast lands, but two things then go
// uncovered unless the consumer ALSO re-renders on a clock tick:
//   1. The clock crossing a lot's close time with no new broadcast — the lot
//      should flip to `awaiting-resolution`/closed on the client's own tick
//      (ADR 023, `lot-snapshot.ts` header). A view with no tick shows a stale
//      phase until some unrelated broadcast happens to repaint it.
//   2. A broadcast that updated the store but did not repaint THIS consumer
//      (a missed/coalesced `useSyncExternalStore` notify): `useSnapshot`'s
//      `getSnapshot` reads the CURRENT store snapshot on every render, so any
//      re-render — including a clock tick — re-reads the fresh snapshot and the
//      derivation self-heals within one tick.
//
// The lot-detail panel happened to get (1)+(2) for free via its 1 Hz
// `useCountdown`; the browse / auction-detail grids had no tick and got stuck
// showing "Coming Soon" after pre-bidding opened (BUG-887). Rather than make
// every view remember to add a ticker, the realtime lot hooks
// (`useRealtimeLot`, `useRealtimeLotUpdate`) call this hook internally, so
// every consumer of live lot state re-derives correctly with zero extra wiring.
//
// Implementation: a single module-level `setInterval` PER interval length,
// ref-counted and shared across all subscribers via `useSyncExternalStore`. A
// grid of N cards shares ONE timer, not N. The snapshot is a monotonic tick
// counter (stable between ticks, changes each tick) so React re-renders exactly
// once per interval per subscriber. SSR returns 0 (no timers on the server).

import { useCallback, useSyncExternalStore } from 'react';

interface Ticker {
    interval: ReturnType<typeof setInterval> | null;
    listeners: Set<() => void>;
    tick: number;
}

// One ticker per distinct interval length. Module-level so every hook instance
// at the same cadence shares a single timer.
const tickers = new Map<number, Ticker>();

function getTicker(intervalMs: number): Ticker {
    let ticker = tickers.get(intervalMs);
    if (!ticker) {
        ticker = { interval: null, listeners: new Set(), tick: 0 };
        tickers.set(intervalMs, ticker);
    }
    return ticker;
}

function subscribe(intervalMs: number, listener: () => void): () => void {
    const ticker = getTicker(intervalMs);
    ticker.listeners.add(listener);
    // Start the shared timer on the first subscriber.
    if (ticker.interval === null) {
        ticker.interval = setInterval(() => {
            ticker.tick += 1;
            // Snapshot the set + isolate each notify so one subscriber throwing
            // (or unsubscribing mid-iteration) can't starve the rest — mirrors
            // the in-memory realtime transport's per-handler try/catch.
            for (const l of [...ticker.listeners]) {
                try {
                    l();
                } catch {
                    // A listener is React's useSyncExternalStore notify; it
                    // shouldn't throw, but never let one break the fan-out.
                }
            }
        }, intervalMs);
    }
    return () => {
        ticker.listeners.delete(listener);
        // Stop the shared timer when the last subscriber leaves so idle tabs
        // don't hold a timer open.
        if (ticker.listeners.size === 0 && ticker.interval !== null) {
            clearInterval(ticker.interval);
            ticker.interval = null;
        }
    };
}

/**
 * Subscribe the calling component to a shared periodic tick, forcing a
 * re-render every `intervalMs`. Returns the monotonic tick count (rarely
 * needed — the re-render is the point).
 *
 * @param intervalMs Tick cadence in ms. Defaults to 1000 (1 Hz), matching the
 *   lot-detail countdown so grid phase transitions land on the same cadence.
 *   `null` holds no timer and never ticks (BUG-2100: a hook that must be called
 *   unconditionally but has nothing live to count down, e.g. offline).
 * @returns The current tick count for this cadence (0 when `null`).
 * @sideEffect Registers against a shared module-level `setInterval` (one timer
 *   per distinct `intervalMs`, ref-counted); the timer stops when the last
 *   subscriber unmounts.
 */
export function useRealtimeClockTick(intervalMs: number | null = 1000): number {
    const subscribeFn = useCallback(
        (listener: () => void) => (intervalMs == null ? () => {} : subscribe(intervalMs, listener)),
        [intervalMs],
    );
    const getSnapshot = useCallback(() => (intervalMs == null ? 0 : getTicker(intervalMs).tick), [intervalMs]);
    // SSR: no timers, no ticks — a stable 0 keeps hydration deterministic.
    return useSyncExternalStore(subscribeFn, getSnapshot, () => 0);
}

/** Test-only: stop + clear every shared ticker so tests don't leak timers. */
export function __resetRealtimeClockTickersForTests(): void {
    for (const ticker of tickers.values()) {
        if (ticker.interval !== null) clearInterval(ticker.interval);
    }
    tickers.clear();
}
